import { useState } from 'preact/hooks';
import { api, mensagemDoErro, qs } from '../api';
import { dataCurta, plural } from '../format';
import { useApi } from '../hooks';
import { useSecretarias } from '../lookups';
import { pode } from '../permissoes';
import { usuario } from '../state';
import type { PesquisaDetalhe } from '../types';
import { Campo, Carregando, ErroForm, Modal, Selo, avisar } from '../ui';
import { useRecarga } from './comum';
import { PesquisaModal, type Aba } from './PesquisaModal';
import { CampoData } from '../componentes/Campos';

type Linha = Pick<PesquisaDetalhe, 'id' | 'titulo' | 'status' | 'periodoInicio' | 'periodoFim' | 'secretariaId' | 'respostas' | 'municipiosComMeta' | 'amostraPrevista'>;

const TOM = { RASCUNHO: 'cinza', ABERTA: 'verde', ENCERRADA: 'azul' } as const;
const ROTULO = { RASCUNHO: 'Rascunho', ABERTA: 'Em coleta', ENCERRADA: 'Encerrada' } as const;

export function PesquisasPage() {
  const podeEscrever = pode(usuario.value?.perfil, 'pesquisa:write');
  const { porId } = useSecretarias();
  const [status, setStatus] = useState('');
  const [recarga, recarregar] = useRecarga();
  const lista = useApi<Linha[]>(`/pesquisas${qs({ status })}`, recarga);
  const [aberta, setAberta] = useState<{ id: string; aba: Aba } | null>(null);
  const [criando, setCriando] = useState(false);
  const itens = lista.dados ?? [];

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Pesquisas de satisfação</h1>
          <p class="dica">Você define as perguntas e a meta de entrevistas por município; os pesquisadores lançam as respostas.</p>
        </div>
        {podeEscrever && <button type="button" class="botao primario" onClick={() => setCriando(true)}>+ Nova pesquisa</button>}
      </header>

      <section class="barra-filtros" aria-label="Filtros">
        <label class="campo"><span>Situação</span>
          <select value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
            <option value="">Todas</option><option value="RASCUNHO">Rascunho</option><option value="ABERTA">Em coleta</option><option value="ENCERRADA">Encerrada</option>
          </select>
        </label>
      </section>

      {lista.erro && <ErroForm texto={mensagemDoErro(lista.erro)} />}
      {lista.carregando && !lista.dados && <Carregando />}

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Lista de pesquisas</caption>
          <thead><tr><th scope="col">Pesquisa</th><th scope="col">Sobre</th><th scope="col">Período</th><th scope="col">Situação</th><th scope="col" class="num">Respostas</th><th scope="col" class="num">Municípios</th></tr></thead>
          <tbody>
            {itens.map((p) => (
              <tr key={p.id}>
                <td><button type="button" class="link-linha" onClick={() => setAberta({ id: p.id, aba: podeEscrever ? 'dados' : 'resultado' })}>{p.titulo}</button></td>
                <td>{p.secretariaId ? (porId.get(p.secretariaId)?.sigla ?? 'Secretaria') : 'Governo em geral'}</td>
                <td>{dataCurta(p.periodoInicio, true)} → {dataCurta(p.periodoFim, true)}</td>
                <td><Selo texto={ROTULO[p.status]} tom={TOM[p.status]} /></td>
                <td class="num">{p.respostas}{p.amostraPrevista > 0 && <small> / {p.amostraPrevista}</small>}</td>
                <td class="num">{p.municipiosComMeta}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!lista.carregando && itens.length === 0 && !lista.erro && (
          <div class="vazio-grande">
            <p>Nenhuma pesquisa{status ? ' com essa situação' : ' cadastrada ainda'}.</p>
            {podeEscrever && !status && <button type="button" class="botao primario" onClick={() => setCriando(true)}>Criar a primeira pesquisa</button>}
          </div>
        )}
      </div>

      {criando && <NovaPesquisa onFechar={() => setCriando(false)} onCriada={(id) => { setCriando(false); recarregar(); setAberta({ id, aba: 'perguntas' }); }} />}
      {aberta && <PesquisaModal id={aberta.id} abaInicial={aberta.aba} onFechar={() => setAberta(null)} onMudou={recarregar} />}
    </main>
  );
}

function NovaPesquisa({ onFechar, onCriada }: { onFechar: () => void; onCriada: (id: string) => void }) {
  const { lista: secretarias } = useSecretarias();
  const [titulo, setTitulo] = useState('');
  const [metodologia, setMetodologia] = useState('');
  const [ini, setIni] = useState('');
  const [fim, setFim] = useState('');
  const [secretariaId, setSecretariaId] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    if (fim < ini) return setErro('O fim do período não pode ser anterior ao início.');
    setSalvando(true);
    try {
      const p = await api<PesquisaDetalhe>('/pesquisas', {
        method: 'POST',
        body: { titulo: titulo.trim(), metodologia: metodologia.trim() || null, periodoInicio: ini, periodoFim: fim, secretariaId: secretariaId || null },
      });
      avisar('Pesquisa criada como rascunho. Agora defina as perguntas e as metas.');
      onCriada(p.id);
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Nova pesquisa" onFechar={onFechar}>
      <form class="form" onSubmit={salvar}>
        <Campo rotulo="Título *">{(id) => <input id={id} required minLength={3} maxLength={200} value={titulo} onInput={(e) => setTitulo(e.currentTarget.value)} />}</Campo>
        <Campo rotulo="Sobre o quê" dica="Deixe em “Governo em geral” para medir a aprovação do governo; ou escolha uma secretaria.">
          {(id) => (
            <select id={id} value={secretariaId} onChange={(e) => setSecretariaId(e.currentTarget.value)}>
              <option value="">Governo em geral</option>
              {secretarias.filter((s) => s.ativa).map((s) => <option key={s.id} value={s.id}>{s.sigla} — {s.nome}</option>)}
            </select>
          )}
        </Campo>
        <div class="linha-form">
          <Campo rotulo="Início da coleta *" larg="meio">{(id) => <CampoData id={id} required value={ini} onChange={setIni} />}</Campo>
          <Campo rotulo="Fim da coleta *" larg="meio">{(id) => <CampoData id={id} required value={fim} onChange={setFim} />}</Campo>
        </div>
        <Campo rotulo="Metodologia" dica="Como as pessoas serão escolhidas (ex.: sorteio de domicílios, cotas por sexo e idade). Isso define o quanto a margem de erro é confiável.">
          {(id) => <textarea id={id} rows={3} maxLength={2000} value={metodologia} onInput={(e) => setMetodologia(e.currentTarget.value)} />}
        </Campo>
        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Criando…' : 'Criar rascunho'}</button>
        </div>
      </form>
    </Modal>
  );
}
